import { useEffect, useState } from 'react';
import { useCreateOrderMutation } from 'redux/Api/ordersApi/ordersApi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { resetOrderInfo } from 'redux/ordersDetails/slice';

interface ApiError {
  data: {
    message: string;
  };
}

export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' && error !== null && 'data' in error;
};

export const useOrderSubmission = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [saveOrder, { error, isLoading: mutationLoading }] = useCreateOrderMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (error && isApiError(error)) {
      setApiError(error.data.message);
      setIsSubmitting(false);
    }
  }, [error]);

  const handleSubmit = async (
    draft: boolean,
    validateFields: () => boolean,
    validateDraftAmount: () => boolean,
    errorCountDescription: boolean,
  ) => {
    if (errorCountDescription) {
      return;
    }

    if (draft) {
      if (!validateDraftAmount()) {
        return;
      }
    } else if (!validateFields()) {
      return;
    }

    setIsSubmitting(true);

    const {
      date_start,
      date_end,
      timezone,
      unit_price,
      is_remote,
      accept_automatically,
      order_preset_id,
      with_date_start,
      with_date_end,
      add_tax_amount,
      unit_volume,
      use_fast_withdraw,
      is_unit_based,
      amount,
      total_price,
      unit_measure,
      unit_volume_min,
      unit_volume_max,
      contract_kind,
      worker_id,
      ...rest
    } = infoOrderDetail;
    void amount;
    void total_price;
    void worker_id;
    void with_date_start;
    void with_date_end;

    const formattedDateStart = date_start
      ? moment(date_start).format('YYYY-MM-DD HH:mm')
      : null;
    const formattedDateEnd = date_end
      ? moment(date_end).format('YYYY-MM-DD HH:mm')
      : moment(new Date()).format('YYYY-MM-DD 00:00');

    try {
      const result = await saveOrder({
        body: {
          ...rest,
          date_end: formattedDateEnd,
          date_start: formattedDateStart,
          timezone: timezone,
          unit_price: +unit_price,
          is_unit_based: is_unit_based ? 1 : 0,
          unit_measure: unit_measure,
          is_draft: draft,
          unit_volume: +unit_volume,
          is_remote: is_remote === 'Удаленно',
          use_fast_withdraw: use_fast_withdraw ? 1 : 0,
          ...(order_preset_id !== null && { order_preset_id }),
          ...(contract_kind && { unified_contract_type: contract_kind }),
          ...(!is_unit_based && {
            accept_automatically: accept_automatically === 1 ? 1 : 0,
            include_tax_amount: add_tax_amount ? 1 : 0,
          }),
          ...(is_unit_based && {
            unit_volume_min: +unit_volume_min,
            unit_volume_max: unit_volume_max ? +unit_volume_max : null,
          }),
        },
      }).unwrap();

      dispatch(resetOrderInfo());
      navigate(`/orders/${result.id}`);
    } catch (err) {
      console.error('Ошибка создания заказа', err);
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    isLoading: mutationLoading || isSubmitting,
    apiError,
    setApiError,
  };
};
